import {useEffect, useState} from "react";
import {io} from "socket.io-client";
import './App.css';

/**
 * Client code looks for server at given https domain through CloudFlare tunnel in production (i.e. hosted on CloudFlare Pages)
 * Otherwise, in dev mode, it will look at the same IP on port 3001
 */
const socket = io(
    process.env.NODE_ENV === "production"
        ? "https://b438server.youthcraft.net" // This only works when the server is running on my laptop through my CloudFlare tunnel
        : ":3001"
);

function PlayerList({playerList, playerId}) {
    return (
        <div className={"playerList"}>
            <h2>Player List</h2>
            <ul>
                {playerList.map((player) => {
                    let displayName;

                    if (player.id === playerId) {
                        displayName = <strong>{player.name} (You)</strong>;
                    } else {
                        displayName = player.name;
                    }

                    return <li
                        key={player.id}>{player.gameHost && "👑"} {displayName} {!player.connected && " (Disconnected)"} {player.waiting && " (Waiting)"} - {player.numberOfCards} {(player.isTurn && player.id === playerId) && " <- Your turn"} {(player.isTurn && player.id !== playerId) && " <- Their turn"}</li>;
                })}
            </ul>
        </div>
    )
}

function Card({id, value, suit, inHand, isTurn, discardTop, openSuitPicker}) {
    const suitEmojiMap = {
        clubs: "♣️",
        spades: "♠️",
        hearts: "♥️",
        diamonds: "♦️"
    };

    let playable = (inHand && isTurn && discardTop && (discardTop.suit === suit || discardTop.value === value || value === "8"));

    const suitEmoji = suitEmojiMap[suit] || ""
    const cardStyle = {
        cursor: playable ? "pointer" : "default",
        border: playable ? "3px solid green" : ""
    }

    const clickAction = () => {
        if (value === "8" && playable) {
            openSuitPicker(id)
        } else if (playable) {
            socket.emit("playCard", id)
        }
    }
    return (
        <div className="card" onClick={clickAction} style={cardStyle}>
            <div className="cardText">
                <p className="cardValue">{value}</p>
                {(value !== "8" || inHand) ? (<p className={"cardSuit"}>{suitEmoji}</p>) : null}
            </div>
            {(value === "8" && !inHand) ? (<><span className={"cardSuitEight"}>{suitEmoji}</span><span
                className={"zanyEmoji"}>🤪</span></>) : null}
        </div>
    )
}


function DrawCard({isTurn, deckLength, canDraw}) {

    const cardStyle = {
        cursor: (isTurn && canDraw) ? "pointer" : "default",
        border: (isTurn && canDraw) ? "3px solid green" : "",
        backgroundColor: "#3f3f3f",
        alignItems: "center",
        justifyContent: "center"
    }
    return (
        <div className="card" onClick={() => socket.emit("drawCard")} style={cardStyle}>
            <p style={{color: "white", textAlign: "center"}}>{deckLength > 0 ? "Draw\n Card" : "⟳\n Reshuffle"}</p>
        </div>
    )
}

function Hand({cards, isTurn, discardTop, openSuitPicker, canDraw}) {
    return (
        <div className={"hand"}>
            <div className="handCards">

                {cards.map((card) => (
                    <Card key={card.id} id={card.id} value={card.value} suit={card.suit} inHand={true} isTurn={isTurn}
                          discardTop={discardTop} openSuitPicker={openSuitPicker}/>
                ))}
            </div>
            {(isTurn && !canDraw) ? (<button onClick={() => socket.emit("skipTurn")}>Skip</button>) : null}
        </div>
    )
}

function Lobby({playerList, gameStarted, playerId}) {

    const isHost = playerList.find(p => p.id === playerId)?.gameHost;

    return (
        <div className={"lobby"}>
            <PlayerList playerList={playerList} playerId={playerId}></PlayerList>

            {gameStarted ?
                (isHost ? (
                        <button onClick={() => socket.emit("stopGame")}>
                            Stop Game
                        </button>) : null
                ) :
                (
                    <button onClick={() => socket.emit("startGame")}>
                        Start Game
                    </button>
                )
            }
        </div>
    )
}

function DiscardDeck({discardTop, isTurn, deckLength, canDraw}) {
    return (
        <div className="discardDeck">
            <Card value={discardTop.value} suit={discardTop.suit}/>
            <DrawCard isTurn={isTurn} deckLength={deckLength} canDraw={canDraw}></DrawCard>
        </div>
    )
}

function App() {
    const [playerList, setPlayerList] = useState([]);
    const [gameStarted, setGameStarted] = useState(false);
    const [playerData, setPlayerData] = useState({cards: []});
    const [discardDeck, setDiscardDeck] = useState([]);
    const [playerId, setPlayerId] = useState(localStorage.getItem("playerId"));
    const [discardTop, setDiscardTop] = useState(discardDeck[discardDeck.length - 1]);

    const [suitPickerOpen, setSuitPickerOpen] = useState(false);
    const [pendingCardId, setPendingCardId] = useState(null);
    const [winnerName, setWinnerName] = useState(null);
    const [deckLength, setDeckLength] = useState(0);

    const openSuitPicker = (cardId) => {
        setPendingCardId(cardId);
        setSuitPickerOpen(true);
    };

    const closeSuitPicker = () => {
        setSuitPickerOpen(false);
        setPendingCardId(null);
    }

    const chooseSuit = (suit) => {
        socket.emit("playCard", pendingCardId, suit);
        setSuitPickerOpen(false);
        setPendingCardId(null);
    };


    useEffect(() => {
        socket.emit("join", playerId);
        socket.on("playerList", (list) => setPlayerList(list));
        socket.on("gameStarted", (started) => setGameStarted(started));
        socket.on("playerId", (id) => {
            localStorage.setItem("playerId", id);
            setPlayerId(id);
        });
        socket.on("playerData", (data) => setPlayerData(data));
        socket.on("discardDeck", (deck) => {
            setDiscardDeck(deck);
            setDiscardTop(deck[deck.length - 1]);
        });
        socket.on("winnerName", (winnerName) => setWinnerName(winnerName));
        socket.on("deckLength", (deckLength) => setDeckLength(deckLength));

        return () => {
            socket.off("playerList");
            socket.off("gameStarted");
            socket.off("playerId");
            socket.off("playerData");
            socket.off("discardDeck");
            socket.off("winnerName");
            socket.off("deckLength")
        };
    }, [playerId]);

    return (

        <div className="gameWindow">
            <Lobby playerList={playerList} gameStarted={gameStarted} playerId={playerId}/>
            {discardTop && (
                <DiscardDeck
                    discardTop={discardTop}
                    isTurn={playerList.find(p => p.id === playerId)?.isTurn}
                    deckLength={deckLength}
                    canDraw={playerData.canDraw}
                />
            )}
            {suitPickerOpen && (
                <div className={"suitPicker"}>
                    <div className={"suitPickerWindow"}>
                        <span onClick={closeSuitPicker}>×</span>
                        <span onClick={closeSuitPicker}>×</span>
                        <h3>Choose a suit</h3>
                        <div className="suitButtons">
                            <button onClick={() => chooseSuit("clubs")}>♣️</button>
                            <button onClick={() => chooseSuit("spades")}>♠️</button>
                            <button onClick={() => chooseSuit("hearts")}>♥️</button>
                            <button onClick={() => chooseSuit("diamonds")}>♦️</button>
                        </div>
                    </div>
                </div>
            )}
            {winnerName && (
                <h2 className={"winner"} style={{textAlign: "center"}}> Winner: {winnerName}</h2>
            )}
            {playerData && (
                <Hand
                    cards={playerData.cards}
                    isTurn={playerList.find(p => p.id === playerId)?.isTurn}
                    canDraw={playerData.canDraw}
                    discardTop={discardTop}
                    openSuitPicker={openSuitPicker}
                />
            )}
        </div>
    );
}


export default App;
